import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteNavbar from '@/components/feature/SiteNavbar';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import { usePageMeta } from '@/hooks/usePageMeta';
import { trendingList } from '@/mocks/trending';
import { categories } from '@/mocks/articles';

const trendIcon: Record<string, { icon: string; cls: string; label: string }> = {
  up: { icon: 'ri-arrow-up-line', cls: 'text-primary-600', label: '上升' },
  down: { icon: 'ri-arrow-down-line', cls: 'text-secondary-600', label: '回落' },
  steady: { icon: 'ri-subtract-line', cls: 'text-foreground-400', label: '持平' },
  new: { icon: 'ri-flashlight-fill', cls: 'text-accent-600', label: '新上榜' },
};

const boardCategories = [
  { id: 'all', name: '全部' },
  ...categories.filter((c) => c.id !== 'all').map((c) => ({ id: c.id, name: c.name })),
];

export default function RankingPage() {
  const [category, setCategory] = useState('all');
  const [sort, setSort] = useState<'rank' | 'heat'>('rank');
  const [keyword, setKeyword] = useState('');

  usePageMeta({
    title: '今日热搜榜完整榜单 - 黑料今日热点事件与热门话题排行',
    description:
      '黑料今日热搜榜完整榜单，实时呈现今日热点事件与热门话题的讨论量排行，支持按影视、音乐、综艺、品牌、粉丝分类筛选与关键词搜索，快速找到今天值得关注的焦点。',
    keywords: '黑料今日热搜榜,今日热搜盘点,热点事件排行,热门话题榜单,今日焦点',
    path: '/ranking',
    jsonLd: useMemo(
      () => [
        {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: '今日热搜榜完整榜单',
          description: '按讨论量排序的今日娱乐热点话题集合。',
          inLanguage: 'zh-CN',
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://heiliao-today.example/' },
            { '@type': 'ListItem', position: 2, name: '热搜榜', item: 'https://heiliao-today.example/ranking' },
          ],
        },
      ],
      []
    ),
  });

  const activeName = boardCategories.find((c) => c.id === category)?.name;

  const list = useMemo(() => {
    const filtered = trendingList.filter((item) => {
      const matchCategory = category === 'all' || item.category === activeName;
      const matchKeyword = !keyword.trim() || item.title.toLowerCase().includes(keyword.trim().toLowerCase());
      return matchCategory && matchKeyword;
    });
    return sort === 'heat' ? [...filtered].sort((a, b) => b.heat - a.heat) : filtered;
  }, [category, keyword, sort, activeName]);

  const maxHeat = Math.max(...trendingList.map((item) => item.heat));

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <SiteNavbar />

      <main className="flex-1 pt-16">
        <section className="w-full bg-secondary-950">
          <div className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
            <nav aria-label="面包屑导航" className="mb-6">
              <ol className="flex items-center gap-2 text-xs text-secondary-300/80">
                <li>
                  <Link to="/" className="hover:text-accent-300 transition-colors">
                    首页
                  </Link>
                </li>
                <li aria-hidden="true">
                  <i className="ri-arrow-right-s-line"></i>
                </li>
                <li className="text-accent-300">热搜榜</li>
              </ol>
            </nav>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50">
              今日热搜榜完整榜单
            </h1>
            <p className="mt-4 text-sm md:text-base text-background-100/75 leading-7 max-w-3xl">
              收录当天讨论量较高的娱乐话题，按热度值排序并标注升降走势。热度值综合讨论、转发、评论与时间衰减计算，
              只代表相对高低，不代表任何事实判断。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <span className="px-3 py-1.5 rounded-md bg-background-50/10 text-xs text-background-100">
                共 {trendingList.length} 条热点
              </span>
              <span className="px-3 py-1.5 rounded-md bg-background-50/10 text-xs text-background-100 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-400 animate-soft-pulse"></span>
                今日 10:00 更新
              </span>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-8">
              <div className="flex flex-wrap gap-2">
                {boardCategories.map((cat) => {
                  const active = category === cat.id;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setCategory(cat.id)}
                      aria-pressed={active}
                      className={`px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap ${
                        active
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                      }`}
                    >
                      {cat.name}
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex items-center gap-2 bg-background-100 rounded-full pl-4 pr-3 h-11 sm:w-64">
                  <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                    <i className="ri-search-line" aria-hidden="true"></i>
                  </span>
                  <input
                    type="search"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="搜索榜单话题"
                    aria-label="搜索榜单话题"
                    className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                  />
                </div>
                <div className="flex p-1 rounded-full bg-background-100 h-11">
                  <button
                    type="button"
                    aria-pressed={sort === 'rank'}
                    onClick={() => setSort('rank')}
                    className={`px-4 py-1 rounded-full text-sm transition-colors ${
                      sort === 'rank' ? 'bg-foreground-950 text-background-50' : 'text-foreground-600'
                    }`}
                  >
                    按排名
                  </button>
                  <button
                    type="button"
                    aria-pressed={sort === 'heat'}
                    onClick={() => setSort('heat')}
                    className={`px-4 py-1 rounded-full text-sm transition-colors ${
                      sort === 'heat' ? 'bg-foreground-950 text-background-50' : 'text-foreground-600'
                    }`}
                  >
                    按热度
                  </button>
                </div>
              </div>
            </div>

            {list.length > 0 ? (
              <ol className="flex flex-col">
                {list.map((item, index) => {
                  const trend = trendIcon[item.trend];
                  const to = item.articleId ? `/article/${item.articleId}` : '/ranking';
                  return (
                    <li key={item.title}>
                      <Link
                        to={to}
                        className="group flex flex-col md:flex-row md:items-center gap-3 md:gap-6 py-5 border-b border-background-200 hover:bg-background-100/70 transition-colors px-2 -mx-2 rounded-md"
                      >
                        <span className="flex items-center gap-4 md:w-40 shrink-0">
                          <span
                            className={`w-10 h-10 shrink-0 rounded-md flex items-center justify-center font-heading text-lg font-bold ${
                              index < 3
                                ? 'bg-primary-500 text-background-50'
                                : 'bg-background-100 text-foreground-500 border border-background-300'
                            }`}
                          >
                            {sort === 'heat' ? index + 1 : item.rank}
                          </span>
                          <span className={`flex items-center gap-1 text-xs ${trend.cls}`}>
                            <i className={trend.icon} aria-hidden="true"></i>
                            {trend.label}
                          </span>
                        </span>

                        <span className="flex-1 min-w-0">
                          <span className="block font-heading text-base md:text-lg font-semibold text-foreground-950 group-hover:text-primary-700 transition-colors leading-7">
                            {item.title}
                          </span>
                          <span className="mt-2 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                            <span className="px-2 py-0.5 rounded bg-secondary-100 text-secondary-900">
                              {item.category}
                            </span>
                            <span className="flex items-center gap-1">
                              <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
                              热度 {item.heat.toLocaleString()}
                            </span>
                          </span>
                        </span>

                        <span className="md:w-40 shrink-0 flex items-center gap-3">
                          <span className="flex-1 h-2 rounded-full bg-background-200 overflow-hidden">
                            <span
                              className="block h-full rounded-full bg-gradient-to-r from-primary-400 to-accent-500"
                              style={{ width: `${Math.round((item.heat / maxHeat) * 100)}%` }}
                            ></span>
                          </span>
                          <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-600 transition-colors"></i>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <div className="py-16 text-center bg-background-100 rounded-lg border border-dashed border-background-300">
                <p className="font-heading text-lg font-semibold text-foreground-900">没有匹配的榜单条目</p>
                <p className="mt-2 text-sm text-foreground-500">换个关键词或切换分类试试。</p>
                <button
                  type="button"
                  onClick={() => {
                    setKeyword('');
                    setCategory('all');
                  }}
                  className="mt-5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  重置筛选
                </button>
              </div>
            )}

            <p className="mt-8 text-xs text-foreground-500 leading-6">
              说明：榜单仅供了解讨论热度使用，涉及个人隐私或未经核实的话题不会收录。若发现内容有误，可通过
              <Link to="/about" className="text-primary-700 hover:text-primary-600 mx-1">
                关于我们
              </Link>
              页面反馈。
            </p>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}